import React from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Minus, Plus, Trash2, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, total } = useCart();
  const { user, loading } = useAuth();
  const loc = useLocation();

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/30 z-50 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setOpen(false)}
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full max-w-md brand-bg z-50 shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between p-6 border-b brand-border">
          <h2 className="font-serif text-2xl brand-text">Carrinho</h2>
          <button onClick={() => setOpen(false)} aria-label="Fechar" data-testid="cart-close">
            <X size={22} />
          </button>
        </div>

        {!loading && !user ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-testid="cart-locked">
            <Lock size={32} className="brand-primary-text mb-4" />
            <h3 className="font-serif text-2xl brand-text mb-3">Acesso reservado</h3>
            <p className="text-sm brand-muted mb-8 max-w-xs">
              O carrinho está disponível apenas para clientes registados. Inicie sessão ou crie uma conta para continuar.
            </p>
            <Link
              to="/entrar"
              state={{ from: loc.pathname }}
              onClick={() => setOpen(false)}
              className="btn-primary w-full mb-3"
              data-testid="cart-login-link"
            >
              Entrar
            </Link>
            <Link
              to="/registar"
              onClick={() => setOpen(false)}
              className="btn-ghost w-full"
              data-testid="cart-register-link"
            >
              Criar conta
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 ? (
                <p className="text-sm brand-muted text-center py-12">O seu carrinho está vazio.</p>
              ) : (
                items.map((it) => (
                  <div key={it.product_id} className="flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                    <div className="w-20 h-24 brand-surface overflow-hidden flex-shrink-0">
                      <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-serif text-lg brand-text">{it.name}</h4>
                      <p className="text-sm brand-muted">{eur(it.price)}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-1 brand-border border" data-testid={`cart-dec-${it.product_id}`}><Minus size={14} /></button>
                        <span className="text-sm w-6 text-center" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-1 brand-border border" data-testid={`cart-inc-${it.product_id}`}><Plus size={14} /></button>
                        <button onClick={() => remove(it.product_id)} className="ml-auto brand-muted hover:text-red-700" data-testid={`cart-remove-${it.product_id}`}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="p-6 border-t brand-border space-y-4">
                <div className="flex justify-between">
                  <span className="label-sm">Subtotal</span>
                  <span className="font-serif text-xl brand-text" data-testid="cart-total">{eur(total)}</span>
                </div>
                <Link to="/checkout" onClick={() => setOpen(false)} className="btn-primary w-full" data-testid="btn-checkout">
                  Finalizar compra
                </Link>
              </div>
            )}
          </>
        )}
      </aside>
    </>
  );
}
